"use client"

import Link from "next/link"
import { Network, Mail, FileText, Heart } from "lucide-react"
import { motion } from "framer-motion"

export function Footer() {
  return (
    <footer className="relative overflow-hidden border-t">
      <div className="absolute inset-0 theme-bg-hero" />
      
      <div className="container mx-auto px-4 py-12 relative">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <motion.div 
            className="space-y-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center space-x-3">
              <motion.div 
                className="flex h-10 w-10 items-center justify-center rounded-full theme-btn-gradient shadow-lg"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <Network className="h-6 w-6 text-white" />
              </motion.div>
              <span className="text-xl font-bold gradient-text">
                Undernet
              </span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              One of the largest IRC networks in the world, connecting people worldwide through IRC.
            </p>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h3 className="font-semibold mb-4 gradient-text">Quick Links</h3>
            <ul className="space-y-3 text-sm">
              <motion.li whileHover={{ x: 5 }}>
                <Link href="/servers" className="text-muted-foreground hover:text-foreground transition-colors">
                  Server List
                </Link>
              </motion.li>
              <motion.li whileHover={{ x: 5 }}>
                <a href="https://chat.undernet.org" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                  Webchat
                </a>
              </motion.li>
              <motion.li whileHover={{ x: 5 }}>
                <Link href="/support" className="text-muted-foreground hover:text-foreground transition-colors">
                  Support
                </Link>
              </motion.li>
            </ul>
          </motion.div>

          {/* Network */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="font-semibold mb-4 gradient-text">Network</h3>
            <ul className="space-y-3 text-sm">
              <motion.li whileHover={{ x: 5 }}>
                <Link href="/services" className="text-muted-foreground hover:text-foreground transition-colors">
                  Services
                </Link>
              </motion.li>
              <motion.li whileHover={{ x: 5 }}>
                <Link href="/committees" className="text-muted-foreground hover:text-foreground transition-colors">
                  Committees
                </Link>
              </motion.li>
              <motion.li whileHover={{ x: 5 }}>
                <Link href="/news" className="text-muted-foreground hover:text-foreground transition-colors">
                  News
                </Link>
              </motion.li>
              <motion.li whileHover={{ x: 5 }}>
                <Link href="https://charter.undernet.org" className="text-muted-foreground hover:text-foreground transition-colors">
                  Charter
                </Link>
              </motion.li>
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h3 className="font-semibold mb-4 gradient-text">Contact</h3>
            <ul className="space-y-3 text-sm">
              <motion.li whileHover={{ x: 5 }}>
                <a
                  href="mailto:webmasters@undernet.org"
                  className="text-muted-foreground hover:text-foreground flex items-center transition-colors"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  webmasters@undernet.org
                </a>
              </motion.li>
              <motion.li whileHover={{ x: 5 }}>
                <Link href="/sitemap" className="text-muted-foreground hover:text-foreground flex items-center transition-colors">
                  <FileText className="h-4 w-4 mr-2" />
                  Sitemap
                </Link>
              </motion.li>
            </ul>
          </motion.div>
        </div>

        <motion.div 
          className="border-t border-border/50 mt-8 pt-8 text-center text-sm text-muted-foreground"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <p className="flex items-center justify-center gap-2">
            &copy; {new Date().getFullYear()} Undernet IRC Network. Made with{" "}
            <motion.span
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <Heart className="h-4 w-4 text-destructive" />
            </motion.span>{" "}
            for the IRC community.
          </p>
        </motion.div>
      </div>
    </footer>
  )
}
